import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useProjectStore } from "../state/projectStore";
import { useKeyStore } from "../state/keyStore";
import { useUi } from "../state/ui";
import { useToast } from "../state/toast";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { useT } from "../i18n";
import { usedChords } from "../core/grid/segments";
import { barreCount, soundingKey, suggestCapo, CAPO_MAX, TRANSPOSE_MAX, TRANSPOSE_MIN } from "../core/theory/transpose";
import { songPathWithKey } from "../core/songUrl";
import { noteGlyphs, settingLabel, signed } from "./keyText";
import { copyText } from "./copyText";
import { ChevronDownIcon, CloseIcon, LinkIcon, MinusIcon, PlusIcon, ResetIcon } from "./icons";

const NARROW = "(max-width: 640px)";

// Song-header key chip: the entry point to the key · capo panel; shows the current setting.
export function KeyChip() {
  const t = useT();
  const key = useProjectStore((s) => s.analysis?.key);
  const transpose = useKeyStore((s) => s.transpose);
  const capo = useKeyStore((s) => s.capo);
  const open = useUi((s) => s.keySheetOpen);
  const setOpen = useUi((s) => s.setKeySheetOpen);
  if (!key) return null;
  const k = { tonic: key.tonic, quality: key.quality };
  return (
    <button id="key-chip" className={"chip chip-key" + (transpose || capo ? " changed" : "")}
      aria-expanded={open} aria-controls="key-sheet" aria-label={t("key.open", { s: settingLabel(t, k, { transpose, capo }) })}
      onClick={() => setOpen(!open)}>
      {t("analysis.key")} <b>{noteGlyphs(soundingKey(k, transpose))}</b>
      {transpose !== 0 && <span className="sub">{signed(transpose)}</span>}
      {capo > 0 && <span className="sub">· {t("key.capo_tag", { n: capo })}</span>}
      <ChevronDownIcon className="chev" />
    </button>
  );
}

// Key · capo panel. Phones: a bottom sheet over the play dock with the chart scrolled into view above
// it, so chord changes stay visible. Wider screens: a popover under the chip. Non-modal either way.
export function KeySheet() {
  const t = useT();
  const open = useUi((s) => s.keySheetOpen);
  const setOpen = useUi((s) => s.setKeySheetOpen);
  const analysis = useProjectStore((s) => s.analysis);
  const measures = useProjectStore((s) => s.chartMeasures);
  const source = useProjectStore((s) => s.source);
  const { transpose, capo, set, reset } = useKeyStore();
  const narrow = useMediaQuery(NARROW);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const close = () => { setOpen(false); document.getElementById("key-chip")?.focus({ preventScroll: true }); };
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!open) return;
    titleRef.current?.focus({ preventScroll: true });
    if (window.matchMedia(NARROW).matches) {
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      document.getElementById("score")?.scrollIntoView({ block: "start", behavior: reduce ? "auto" : "smooth" });
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeRef.current(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // a new song closes the panel
  useEffect(() => useProjectStore.subscribe((s, prev) => { if (s.source !== prev.source) setOpen(false); }), [setOpen]);

  if (!open || !analysis) return null;
  const key = { tonic: analysis.key.tonic, quality: analysis.key.quality };
  const chords = usedChords(measures);
  const pick = suggestCapo(chords, transpose);

  const copyLink = async () => {
    const ok = await copyText(location.origin + songPathWithKey(source, { transpose, capo }));
    useToast.getState().show(t(ok ? "key.link_copied" : "key.copy_failed"));
  };

  const panel = (
    <div id="key-sheet" className={"key-sheet" + (narrow ? " as-sheet" : "")} role="dialog" aria-modal="false" aria-labelledby="key-sheet-title">
      {narrow && <div className="ks-grab" aria-hidden="true" />}
      <div className="ks-head">
        <h3 id="key-sheet-title" ref={titleRef} tabIndex={-1}>{t("key.title")}</h3>
        <button className="icon-btn" onClick={close} aria-label={t("links.close")} title={t("links.close")}><CloseIcon size={18} /></button>
      </div>
      <div className="ks-row">
        <span className="ks-label">{t("key.transpose")}</span>
        <div className="ks-stepper">
          <button className="ks-step" onClick={() => set({ transpose: transpose - 1 })} disabled={transpose <= TRANSPOSE_MIN} aria-label={t("key.down")} title={t("key.down")}><MinusIcon size={16} /></button>
          <div className="ks-val" aria-live="polite">
            <b>{transpose ? signed(transpose) : t("key.original")}</b>
            <small>
              {transpose
                ? <><em>{t("key.key_of", { k: noteGlyphs(soundingKey(key, transpose)) })}</em> · {t("key.orig_of", { k: noteGlyphs(soundingKey(key, 0)) })}</>
                : t("key.key_of", { k: noteGlyphs(soundingKey(key, 0)) })}
            </small>
          </div>
          <button className="ks-step" onClick={() => set({ transpose: transpose + 1 })} disabled={transpose >= TRANSPOSE_MAX} aria-label={t("key.up")} title={t("key.up")}><PlusIcon size={16} /></button>
        </div>
      </div>
      <div className="ks-row">
        <span className="ks-label">{t("key.capo")}</span>
        <div className="ks-stepper">
          <button className="ks-step" onClick={() => set({ capo: capo - 1 })} disabled={capo <= 0} aria-label={t("key.capo_down")} title={t("key.capo_down")}><MinusIcon size={16} /></button>
          <div className="ks-val" aria-live="polite">
            <b>{capo ? t("key.capo_fret", { n: capo }) : t("key.capo_none")}</b>
            <small>{t("key.barre", { n: barreCount(chords, transpose - capo) })}</small>
          </div>
          <button className="ks-step" onClick={() => set({ capo: capo + 1 })} disabled={capo >= CAPO_MAX} aria-label={t("key.capo_up")} title={t("key.capo_up")}><PlusIcon size={16} /></button>
        </div>
      </div>
      {pick ? (
        <button className={"ks-suggest" + (pick.capo === capo ? " applied" : "")} disabled={pick.capo === capo} onClick={() => set({ capo: pick.capo })}>
          <span><b>{t("key.suggest", { c: pick.capo })}</b> · {t("key.suggest_detail", { a: pick.base, b: pick.barre })}</span>
          <em>{t(pick.capo === capo ? "key.applied" : "key.apply")}</em>
        </button>
      ) : (
        <p className="ks-suggest-none">{t("key.suggest_none")}</p>
      )}
      <div className="ks-foot">
        <button className="ks-pill" onClick={reset} disabled={!transpose && !capo}><ResetIcon size={15} />{t("key.reset")}</button>
        {source.kind === "youtube" && <button className="ks-pill" onClick={copyLink}><LinkIcon size={15} />{t("key.copy_link")}</button>}
        <p className="ks-note">{t("key.note")}</p>
      </div>
    </div>
  );
  // the bottom sheet lives in the app shell (beside the dock); the popover stays in the song header
  const shell = narrow ? document.querySelector(".app") : null;
  return shell ? createPortal(panel, shell) : panel;
}
